import React from "react";
import PropTypes from "prop-types";
import { Row, Col } from "reactstrap";
import { METRIC_LABELS, METRIC_GROUP_LABELS, METRIC_GROUP_ICONS } from "../../../../constants/performance";
import { AvatarPreview, NeoModal, NeoBadge } from "../../../../components/Common";

/**
 * EmployeeDetailModal — Modal com breakdown de métricas do colaborador.
 * Migrado para NeoModal + NeoBadge + Tabela estruturada .neo-table (Taça Outono).
 */
const EmployeeDetailModal = ({ isOpen, onClose, employee }) => {
    if (!employee) return null;

    const breakdown = employee.scoreBreakdown || {};
    const groupIcon = METRIC_GROUP_ICONS[employee.employeeRole] || "mdi-account";
    const groupLabel = METRIC_GROUP_LABELS[employee.employeeRole] || employee.employeeRoleName;

    const MONTH_NAMES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

    const formatPeriod = (period) => {
        if (!period) return "";
        const [year, month] = period.split("-");
        if (!month) return period;
        return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
    };

    // Ordenar métricas por peso (maior peso primeiro) e ignorar chaves de banco irrelevantes
    const sortedMetrics = Object.entries(breakdown)
        .filter(([key]) => METRIC_LABELS[key])
        .sort((a, b) => (b[1].weight || 0) - (a[1].weight || 0));

    const getBadgeColor = (normalized) => {
        if (normalized >= 80) return "success";
        if (normalized >= 50) return "primary";
        if (normalized >= 30) return "warning";
        return "danger";
    };

    const formatRawValue = (key, raw) => {
        if (key === "revenue" || key === "revenueWeighted" || key === "ticketMedio") {
            return `R$ ${Number(raw).toLocaleString("pt-BR", { minimumFractionDigits: 0 })}`;
        }
        if (key.includes("Rate") || key === "assiduidade" || key === "tasksOnTime" || key.includes("conversion") || key.includes("Conversion") || key === "churnRate" || key === "ocupacaoTurmas" || key === "evaluationCoverage" || key === "carteiraHealth") {
            return `${Number(raw).toFixed(1)}%`;
        }
        
        if (typeof raw === "number" && !Number.isInteger(raw)) {
            return Number(raw).toFixed(1);
        }
        
        return String(raw);
    };

    // Header customizado com avatar + info
    const modalTitle = (
        <div className="d-flex align-items-center">
            <AvatarPreview
                src={employee.employeePhotoUrl}
                alt={employee.employeeName}
                size="md"
                className="me-3"
            />
            <div>
                <span className="fw-bold text-brand-primary font-size-15 d-block">{employee.employeeName}</span>
                <div className="d-flex align-items-center gap-2 mt-1">
                    <NeoBadge color="secondary" icon={groupIcon}>
                        {groupLabel}
                    </NeoBadge>
                    <span className="text-muted font-size-12">{formatPeriod(employee.period)}</span>
                </div>
            </div>
        </div>
    );

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={onClose}
            size="xl"
            title=""
        >
            {/* Header customizado dentro do body */}
            <div className="mb-4 pb-3 border-bottom">
                {modalTitle}
            </div>

            {/* Resumo do Score */}
            <Row className="g-3 mb-4">
                <Col xs={4}>
                    <div className="d-flex align-items-start p-2">
                        <div className="me-3 text-brand-accent" style={{ marginTop: '2px' }}>
                            <i className="mdi mdi-star-circle font-size-24"></i>
                        </div>
                        <div>
                            <p className="text-muted mb-0 text-truncate font-size-12 lh-1">Score Final</p>
                            <h5 className="mb-0 mt-1 fw-bold text-brand-accent">{employee.scoreFinal?.toFixed(1)}</h5>
                        </div>
                    </div>
                </Col>
                <Col xs={4}>
                    <div className="d-flex align-items-start p-2">
                        <div className="me-3 text-warning" style={{ marginTop: '2px' }}>
                            <i className="mdi mdi-podium font-size-24"></i>
                        </div>
                        <div>
                            <p className="text-muted mb-0 text-truncate font-size-12 lh-1">Ranking Geral</p>
                            <h5 className="mb-0 mt-1 fw-bold text-brand-primary">{employee.rankingGeral}º</h5>
                            <small className="text-muted font-size-11">de {employee.totalEmployees}</small>
                        </div>
                    </div>
                </Col>
                <Col xs={4}>
                    <div className="d-flex align-items-start p-2">
                        <div className="me-3 text-success" style={{ marginTop: '2px' }}>
                            <i className="mdi mdi-account-group font-size-24"></i>
                        </div>
                        <div>
                            <p className="text-muted mb-0 text-truncate font-size-12 lh-1">No Grupo</p>
                            <h5 className="mb-0 mt-1 fw-bold text-brand-primary">{employee.rankingByRole}º</h5>
                            <small className="text-muted font-size-11">de {employee.totalByRole}</small>
                        </div>
                    </div>
                </Col>
            </Row>

            <div className="border-top mb-3"></div>

            {/* Metrics Breakdown Table */}
            <p className="neo-table-topbar-label mb-2">
                <i className="mdi mdi-chart-bar me-1"></i> Detalhamento das Métricas
            </p>

            <div className="neo-table-wrapper border rounded-3 overflow-hidden">
                <table className="neo-table align-middle">
                    <thead>
                        <tr>
                            <th>Métrica</th>
                            <th style={{ width: '110px' }} className="text-center">Peso</th>
                            <th style={{ width: '220px' }} className="text-end">Valor Real / Meta</th>
                            <th style={{ width: '130px' }} className="text-end">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedMetrics.map(([key, data]) => {
                            const label = METRIC_LABELS[key] || key;
                            const weightPercent = Math.round((data.weight || 0) * 100);
                            const rawStats = employee.metrics?.raw || {};

                            // Gerar descrição detalhada do motivador do Score baseado no RAW do banco
                            let rawDescription = "";
                            if (key === "assiduidade" && rawStats.totalTitular !== undefined) {
                                rawDescription = `Compareceu em ${rawStats.presentTitular} das ${rawStats.totalTitular} aulas designadas`;
                            } else if (key === "attendanceRate" && rawStats.totalAttendees !== undefined) {
                                rawDescription = `Recebeu ${rawStats.totalPresent} alunos das ${rawStats.totalAttendees} reservas totais`;
                            } else if (key === "ocupacaoTurmas" && rawStats.totalMaxCapacity !== undefined) {
                                rawDescription = `Possui ${rawStats.totalEnrolledCount} alunos ocupando as ${rawStats.totalMaxCapacity} vagas disponíveis nas suas turmas`;
                            } else if (key === "trialConversion" && rawStats.trialsInPeriod !== undefined) {
                                rawDescription = `Converteu ${rawStats.trialsConverted} das ${rawStats.trialsInPeriod} aulas experimentais em vendas`;
                            } else if (key === "evaluationCoverage" && rawStats.totalEvaluationsRaw !== undefined) {
                                const enrolledCount = rawStats.uniqueEnrolledCount !== undefined ? rawStats.uniqueEnrolledCount : rawStats.totalEnrolledCount;
                                rawDescription = `Avaliou ${rawStats.evaluatedCount} alunos únicos de ${enrolledCount} matriculados. (Realizou ${rawStats.totalEvaluationsRaw} avaliações totais).`;
                            } else if (key === "carteiraHealth") {
                                const parts = [];
                                if (rawStats.expiringContracts !== undefined) {
                                    if (rawStats.expiringContracts === 0) {
                                        parts.push(`Retenção: nenhum contrato venceu no período`);
                                    } else {
                                        parts.push(`Retenção: ${rawStats.renewedContracts}/${rawStats.expiringContracts} alunos retidos`);
                                    }
                                }
                                if (rawStats.previousVolume > 0) {
                                    const sig = rawStats.netGrowthBruto > 0 ? "+" : "";
                                    parts.push(`Crescimento: ${sig}${rawStats.netGrowthBruto} alunos vs ${rawStats.previousVolume} do mês anterior`);
                                } else if (rawStats.totalEnrolledCount > 0) {
                                    parts.push(`Crescimento: primeiro mês (${rawStats.totalEnrolledCount} alunos)`);
                                }
                                rawDescription = parts.join(" · ");
                            }

                            const isNA = data.notApplicable === true || data.raw === null;

                            return (
                                <tr key={key}>
                                    <td>
                                        <div className="d-flex flex-column py-1">
                                            <span className={`fw-bold font-size-13 ${isNA ? "text-muted opacity-50" : "text-brand-primary"}`}>
                                                {label}
                                            </span>
                                            {isNA ? (
                                                <div className="text-muted font-size-10 fst-italic mt-0.5">
                                                    Sem dados suficientes neste período — peso redistribuído para outras métricas
                                                </div>
                                            ) : rawDescription && (
                                                <div className="text-muted font-size-10 fst-italic mt-0.5">
                                                    {rawDescription}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-center font-size-12 text-muted fw-bold">
                                        {weightPercent}%
                                    </td>
                                    <td className="text-end">
                                        {isNA ? (
                                            <span className="text-muted">—</span>
                                        ) : (
                                            <div className="d-flex flex-column align-items-end py-1">
                                                <span className="fw-bold text-brand-accent font-size-13">
                                                    {formatRawValue(key, data.raw)}
                                                </span>
                                                {data.goal !== null && data.goal !== undefined && (
                                                    <span className="text-muted font-size-10 opacity-75">
                                                        meta: {formatRawValue(key, data.goal)}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {isNA ? (
                                            <NeoBadge color="secondary">N/A</NeoBadge>
                                        ) : (
                                            <NeoBadge color={getBadgeColor(data.normalized)} style={{ minWidth: "75px", textAlign: "center" }}>
                                                {typeof data.normalized === 'number' && !Number.isInteger(data.normalized) ? data.normalized.toFixed(1) : data.normalized} pts
                                            </NeoBadge>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </NeoModal>
    );
};

EmployeeDetailModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    employee: PropTypes.object,
};

export default EmployeeDetailModal;
